/**
 * payments.ts — PAY-02 (2026-08-06). Hooks + helpers for the Pegasus payment ledger.
 *
 * DEC: processor = STRIPE. All charging goes through the `collect-payment` edge
 * function (the ONE provider boundary — pct caps, credit-only surcharge, the
 * payments_live gate, and capability checks are enforced SERVER-side; anything
 * this file shows is preview only). Card data never touches Pegasus: new cards
 * are entered into Stripe-hosted iframe fields (Elements) and become a token.
 *
 * The ledger: pegasus_payment (+ _allocation to invoices, + _refund) — paid
 * state on an invoice is DERIVED from succeeded allocations minus refunds.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";

// ---------- types ----------
export interface PaymentAllocation {
  id: string;
  payment_id: string;
  invoice_id: string | null;
  esc_invoice_no: string | null;
  amount: number;
  /** FIELD-PAY-01: money held for a job (no invoice yet); kept after close. */
  job_id?: string | null;
  /** FIELD-PAY-01: a deposit payment that close_job moved onto the final invoice. */
  applied_from_invoice_id?: string | null;
}
export interface PaymentRefund {
  id: string;
  payment_id: string;
  amount: number;
  reason: string;
  note: string | null;
  status: "pending" | "succeeded" | "failed";
  created_by: string | null;
  created_at: string;
}
/** PAY-04: a chargeback, mirrored from Stripe's charge.dispute.* webhooks. */
export interface PaymentDispute {
  id: string;
  payment_id: string | null;
  provider_dispute_id: string;
  amount: number;
  reason: string | null;
  status: string;
  evidence_due_by: string | null;
  created_at: string;
}
export interface PegasusPayment {
  id: string;
  custno: string;
  amount: number;
  surcharge_amount: number;
  method: "card" | "card_on_file" | "ach" | "check" | "cash" | "other" | "klarna" | "financing";
  /** FIELD-PAY-01: office | portal | field | autopay | lender. */
  source?: string | null;
  status: string;
  provider: "stripe" | "manual";
  provider_payment_intent_id: string | null;
  card_brand: string | null;
  card_last4: string | null;
  card_funding: string | null;
  receipt_url: string | null;
  auth_code: string | null;
  reference: string | null;
  failure_reason: string | null;
  livemode: boolean;
  created_by: string | null;
  created_at: string;
  pegasus_payment_allocation: PaymentAllocation[];
  pegasus_payment_refund: PaymentRefund[];
  pegasus_payment_dispute?: PaymentDispute[];
}
export interface PaymentProfile {
  id: string;
  custno: string;
  provider_customer_id: string;
  default_payment_method_id: string | null;
  card_brand: string | null;
  card_last4: string | null;
  card_funding: string | null;
  consent_at: string | null;
}
export interface PaymentConfig {
  publishable_key: string | null;
  configured: boolean;
  test_mode: boolean;
  surcharge_pct: number;
  surcharge_min_invoice: number;
  surcharge_disclosure: string;
}

// ---------- edge-function call helper ----------
export async function collectPayment<T = Record<string, unknown>>(body: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke("collect-payment", { body });
  if (error) {
    // supabase-js wraps non-2xx; the function's JSON error body is the useful part
    let msg = error.message ?? "Payment call failed";
    try {
      const ctx = (error as { context?: Response }).context;
      if (ctx) {
        const j = await ctx.json();
        if (j?.error) msg = j.error;
      }
    } catch { /* keep msg */ }
    throw new Error(msg);
  }
  if (data && typeof data === "object" && "error" in data && (data as { error?: string }).error) {
    throw new Error((data as { error: string }).error);
  }
  return data as T;
}

// ---------- queries ----------
export function usePaymentConfig() {
  return useQuery({
    queryKey: ["payment_config"],
    staleTime: 5 * 60_000,
    queryFn: () => collectPayment<PaymentConfig>({ action: "config" }),
  });
}

export function useCustomerPaymentProfile(custno: string | null) {
  return useQuery({
    queryKey: ["payment_profile", custno],
    enabled: !!custno,
    queryFn: async (): Promise<PaymentProfile | null> => {
      const { data } = await supabase
        .from("pegasus_customer_payment_profile")
        .select("id, custno, provider_customer_id, default_payment_method_id, card_brand, card_last4, card_funding, consent_at")
        .eq("custno", custno!)
        .eq("provider", "stripe")
        .maybeSingle();
      return (data as PaymentProfile | null) ?? null;
    },
  });
}

export function useCustomerPayments(custno: string | null) {
  return useQuery({
    queryKey: ["payments", custno],
    enabled: !!custno,
    queryFn: async (): Promise<PegasusPayment[]> => {
      const { data } = await supabase
        .from("pegasus_payment")
        .select("*, pegasus_payment_allocation(*), pegasus_payment_refund(*), pegasus_payment_dispute(*)")
        .eq("custno", custno!)
        .order("created_at", { ascending: false })
        .order("id"); // stable-sort tiebreak — rows must not jump on update
      return (data ?? []) as PegasusPayment[];
    },
  });
}

/** PAY-04: payment terms — days until an unpaid issued invoice reads Past due. */
export function usePaymentTerms() {
  return useQuery({
    queryKey: ["payment_terms_days"],
    staleTime: 10 * 60_000,
    queryFn: async (): Promise<number> => {
      const { data } = await supabase.from("org_setting").select("value").eq("key", "payment_terms_days").maybeSingle();
      const n = Number(data?.value ?? 30);
      return isFinite(n) && n > 0 ? n : 30;
    },
  });
}

/** Derived per-invoice paid math from the ledger (never a manual flag). */
export function invoicePaidMap(payments: PegasusPayment[] | undefined): Map<string, number> {
  const paid = new Map<string, number>();
  for (const p of payments ?? []) {
    // "processing" counts too: an in-flight card charge HOLDS the balance so the
    // office can't double-collect during the webhook window; it un-holds on failure.
    if (!["succeeded", "partially_refunded", "recorded", "refunded", "processing"].includes(p.status)) continue;
    const gross = p.pegasus_payment_allocation ?? [];
    const grossTotal = gross.reduce((s, a) => s + Number(a.amount), 0) || 1;
    // Refunds can include the processing fee (card got amount+fee back) — only
    // the INVOICE portion reduces allocations, so cap at the payment amount.
    const refunded = Math.min(
      (p.pegasus_payment_refund ?? [])
        .filter((r) => r.status === "succeeded")
        .reduce((s, r) => s + Number(r.amount), 0),
      Number(p.amount),
    );
    for (const a of gross) {
      if (!a.invoice_id) continue;
      // refunds reduce allocations proportionally (v1 rule; exact-invoice refunds later)
      const share = Number(a.amount) - refunded * (Number(a.amount) / grossTotal);
      paid.set(a.invoice_id, (paid.get(a.invoice_id) ?? 0) + share);
    }
  }
  return paid;
}

/** Statuses whose money counts toward an invoice (an in-flight payment holds it). */
export const COUNTED_STATUSES = ["succeeded", "partially_refunded", "recorded", "refunded", "processing"];

/** One payment line on a customer document (the printed invoice, the office preview). */
export interface DocPayLine {
  key: string;
  label: string;
  method: string;
  reference: string;
  amount: number;
  feeNote: string | null;
  /** When the money came in (ISO) — lines read oldest first. */
  at: string;
}

/**
 * FIELD-PAY-01 — the payment lines a customer document shows, from the ledger,
 * so every renderer says the same thing (PAY-02's "third renderer" lesson).
 * `match` picks the allocations that belong on the document; `isDeposit` says
 * which of them came from the deposit. Each line is net of refunds (capped at
 * the payment) — the same rule as invoicePaidMap and v_allocation_applied.
 *   · Deposit paid <date> — the deposit's payment (moved onto the final invoice at close)
 *   · Paid at completion <date> — taken by the installer on site
 *   · Financing funded <date> — the lender's money
 *   · Payment received <date> — anything else
 */
export function docPaymentLines(
  ledger: PegasusPayment[] | undefined,
  match: (a: PaymentAllocation) => boolean,
  isDeposit: (a: PaymentAllocation) => boolean = (a) => !!a.applied_from_invoice_id,
): DocPayLine[] {
  const fmt = (iso: string) => new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  const usd = (n: number) => n.toLocaleString("en-US", { style: "currency", currency: "USD" });
  const out: DocPayLine[] = [];
  for (const p of ledger ?? []) {
    if (!COUNTED_STATUSES.includes(p.status)) continue;
    const allocs = p.pegasus_payment_allocation ?? [];
    const gross = allocs.reduce((s, x) => s + Number(x.amount), 0) || 1;
    const refunded = Math.min(
      (p.pegasus_payment_refund ?? []).filter((r) => r.status === "succeeded").reduce((s, r) => s + Number(r.amount), 0),
      Number(p.amount),
    );
    const fee = Number(p.surcharge_amount ?? 0);
    for (const a of allocs) {
      if (!match(a)) continue;
      const amount = Number(a.amount) - refunded * (Number(a.amount) / gross);
      if (amount <= 0.005) continue;
      const feeShare = fee * (Number(a.amount) / gross);
      const when = fmt(p.created_at);
      const label = p.status === "processing"
        ? `Payment processing (bank payments take a few days) ${when}`
        : isDeposit(a) ? `Deposit paid ${when}`
        : p.source === "lender" || p.method === "financing" ? `Financing funded ${when}`
        : p.source === "field" ? `Paid at completion ${when}`
        : `Payment received ${when}`;
      out.push({
        key: a.id,
        at: p.created_at,
        label,
        method: p.method === "card" || p.method === "card_on_file"
          ? `${cardBrandLabel(p.card_brand)} ····${p.card_last4 ?? ""}`
          : p.method === "ach"
            ? `${p.card_brand || "Bank"}${p.card_last4 ? ` ····${p.card_last4}` : ""} bank payment`
            : p.method === "check" && p.reference
              ? `Check #${p.reference}`
              : (p.method.charAt(0).toUpperCase() + p.method.slice(1)),
        // reference slot carries the card authorization code or a lender's
        // approval # (Seth 2026-08-06); a check's number rides in "Check #".
        reference: [p.method === "check" ? null : p.reference, p.auth_code ? `Auth ${p.auth_code}` : null].filter(Boolean).join(" · "),
        amount,
        // Seth 2026-08-07: the processing fee shows ON the invoice — the card
        // statement (invoice amount + fee) must reconcile to this document.
        feeNote: feeShare > 0.005
          ? (allocs.length > 1
              ? `Includes a ${usd(feeShare)} card processing fee share (payment split across invoices)`
              : `A ${usd(feeShare)} card processing fee was added — card charged ${usd(Number(p.amount) + fee)} in total`)
          : null,
      });
    }
  }
  // Oldest first — a document reads in the order the money came in.
  return out.sort((x, y) => x.at.localeCompare(y.at) || x.key.localeCompare(y.key));
}

/** AR-360: what a collect call allocates to — a Pegasus invoice OR an ESC
 *  invoice number (mirror-era). The edge fn + allocation table take both. */
export interface AllocInput { invoice_id?: string | null; esc_invoice_no?: string | null; amount: number }

/** AR-360: same derived-paid math, keyed by ESC invoice number — a check
 *  recorded in Pegasus against ESC invoices shows on those rows immediately
 *  (ESC itself still gets posted until cutover; the mirror refreshes overnight). */
export function escInvoicePaidMap(payments: PegasusPayment[] | undefined): Map<string, number> {
  const paid = new Map<string, number>();
  for (const p of payments ?? []) {
    if (!["succeeded", "partially_refunded", "recorded", "refunded", "processing"].includes(p.status)) continue;
    const gross = p.pegasus_payment_allocation ?? [];
    const grossTotal = gross.reduce((s, a) => s + Number(a.amount), 0) || 1;
    const refunded = Math.min(
      (p.pegasus_payment_refund ?? [])
        .filter((r) => r.status === "succeeded")
        .reduce((s, r) => s + Number(r.amount), 0),
      Number(p.amount),
    );
    for (const a of gross) {
      if (!a.esc_invoice_no) continue;
      const share = Number(a.amount) - refunded * (Number(a.amount) / grossTotal);
      paid.set(a.esc_invoice_no, (paid.get(a.esc_invoice_no) ?? 0) + share);
    }
  }
  return paid;
}

// ---------- mutations ----------
function useInvalidatePayments(custno: string | null) {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: ["payments", custno] });
    qc.invalidateQueries({ queryKey: ["payment_profile", custno] });
    qc.invalidateQueries({ queryKey: ["pegasus_customer_invoices", custno] });
  };
}

export function useRecordManualPayment(custno: string | null) {
  const inval = useInvalidatePayments(custno);
  return useMutation({
    mutationFn: (v: { amount: number; method: "check" | "cash" | "other"; reference?: string; allocations: AllocInput[] }) =>
      collectPayment({ action: "record_manual", custno, ...v }),
    onSuccess: inval,
  });
}

export function useChargeSavedCard(custno: string | null) {
  const inval = useInvalidatePayments(custno);
  return useMutation({
    mutationFn: (v: { amount: number; allocations: AllocInput[] }) =>
      collectPayment({ action: "charge_saved", custno, ...v }),
    onSuccess: inval,
  });
}

export function useChargeNewCard(custno: string | null) {
  const inval = useInvalidatePayments(custno);
  return useMutation({
    mutationFn: (v: {
      customer_name?: string | null;
      amount: number;
      allocations: AllocInput[];
      payment_method_id: string;
      save_card: boolean;
      consent_text?: string;
    }) => collectPayment({ action: "charge_new_card", custno, ...v }),
    onSuccess: inval,
  });
}

export function useRefundPayment(custno: string | null) {
  const inval = useInvalidatePayments(custno);
  return useMutation({
    // The edge function returns the new refund row's id — PAY-02c attaches
    // uploaded documentation (email correspondence etc.) to exactly that refund.
    mutationFn: (v: { payment_id: string; amount: number; reason: string; note?: string }) =>
      collectPayment<{ ok: boolean; refund_id?: string; status?: string }>({ action: "refund", ...v }),
    onSuccess: inval,
  });
}

// ---------- Stripe.js loader (js.stripe.com is the REQUIRED source; no npm dep) ----------
export interface StripeCardElement {
  mount: (sel: HTMLElement) => void;
  unmount: () => void;
  on: (ev: string, cb: (e: { complete?: boolean; error?: { message: string } }) => void) => void;
}
export interface StripeElements {
  create: (kind: string, opts?: Record<string, unknown>) => StripeCardElement;
}
export interface StripeJs {
  /** No-arg = raw card element (office modal). With { clientSecret } = an
   *  intent-bound Elements group for the Payment Element (portal, PAY-03). */
  elements: (opts?: Record<string, unknown>) => StripeElements;
  createPaymentMethod: (opts: Record<string, unknown>) => Promise<{ paymentMethod?: { id: string }; error?: { message: string } }>;
  handleNextAction: (opts: { clientSecret: string }) => Promise<{ error?: { message: string } }>;
  /** PAY-03: confirm a Payment Element (card or US bank account). */
  confirmPayment: (opts: Record<string, unknown>) => Promise<{ error?: { message?: string }; paymentIntent?: { id?: string; status?: string } }>;
}
declare global {
  interface Window { Stripe?: (pk: string) => StripeJs }
}
let stripeLoad: Promise<StripeJs | null> | null = null;
export function loadStripeJs(publishableKey: string): Promise<StripeJs | null> {
  if (!stripeLoad) {
    stripeLoad = new Promise((resolve) => {
      if (window.Stripe) return resolve(window.Stripe(publishableKey));
      const s = document.createElement("script");
      s.src = "https://js.stripe.com/v3";
      s.onload = () => resolve(window.Stripe ? window.Stripe(publishableKey) : null);
      s.onerror = () => resolve(null);
      document.head.appendChild(s);
    });
  }
  return stripeLoad;
}

export const moneyExact = (n: number) =>
  n.toLocaleString("en-US", { style: "currency", currency: "USD" });

/** Card networks report lowercase brand slugs; customers should read proper names. */
export function cardBrandLabel(brand: string | null | undefined): string {
  const map: Record<string, string> = {
    visa: "Visa", mastercard: "Mastercard", amex: "American Express", discover: "Discover",
    diners: "Diners Club", jcb: "JCB", unionpay: "UnionPay",
  };
  if (!brand) return "Card";
  return map[brand.toLowerCase()] ?? brand.charAt(0).toUpperCase() + brand.slice(1);
}

export const METHOD_LABEL: Record<string, string> = {
  card: "Card",
  card_on_file: "Card on file",
  ach: "Bank payment",
  check: "Check",
  cash: "Cash",
  klarna: "Klarna",
  financing: "Lender funding",
  other: "Other",
};
